import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows } from "@/lib/adminApi";
import {
  VISITOR_STATUS,
  VISITOR_TYPE,
  metaFor,
  optionsFrom,
  type Visitor,
} from "@/lib/adminModules";
import {
  DataTable,
  ErrorBanner,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnGhost,
  btnPrimary,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate, formatRupees } from "@/pages/admin/format";
import { exportToCsv, exportToPdf } from "@/pages/admin/exportUtils";
import {
  buildCustomerReportRows,
  summarizeCustomers,
  type CustomerReportRow,
} from "@/lib/adminReports";

export default function AdminCustomerReports() {
  const [rows, setRows] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Visitor>("visitors", "total_spend"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load customer data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reportRows = useMemo<CustomerReportRow[]>(() => {
    const query = search.trim().toLowerCase();
    return buildCustomerReportRows(rows).filter((row) => {
      if (typeFilter !== "all" && row.visitor_type !== typeFilter) return false;
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.name, row.email, row.phone, row.location].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, typeFilter, statusFilter]);

  const summary = useMemo(() => summarizeCustomers(reportRows), [reportRows]);

  const columns: Column<CustomerReportRow>[] = [
    {
      key: "name",
      header: "Customer",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.email || row.phone || "—"}</p>
        </div>
      ),
    },
    { key: "type", header: "Type", render: (row) => <StatusPill {...metaFor(VISITOR_TYPE, row.visitor_type)} /> },
    { key: "location", header: "Location", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.location || "—"}</span> },
    { key: "bookings", header: "Bookings", render: (row) => <span className="text-sm text-foreground-700">{row.bookings}</span> },
    { key: "visits", header: "Visits", render: (row) => <span className="text-sm text-foreground-700">{row.visits}</span> },
    { key: "spend", header: "Lifetime spend", render: (row) => <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.spend)}</span> },
    { key: "last", header: "Last visit", render: (row) => <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.last_visit)}</span> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(VISITOR_STATUS, row.status)} /> },
  ];

  const exportColumns = [
    { header: "Customer", value: (row: CustomerReportRow) => row.name },
    { header: "Email", value: (row: CustomerReportRow) => row.email },
    { header: "Phone", value: (row: CustomerReportRow) => row.phone },
    { header: "Type", value: (row: CustomerReportRow) => row.visitor_type },
    { header: "Location", value: (row: CustomerReportRow) => row.location },
    { header: "Bookings", value: (row: CustomerReportRow) => `${row.bookings}` },
    { header: "Visits", value: (row: CustomerReportRow) => `${row.visits}` },
    { header: "Lifetime spend", value: (row: CustomerReportRow) => `${row.spend}` },
    { header: "Last visit", value: (row: CustomerReportRow) => row.last_visit || "" },
    { header: "Status", value: (row: CustomerReportRow) => row.status },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Reports"
        title="Customer Reports"
        subtitle="Who is visiting, how often they return and how much they spend — filter the base, then export it as CSV or PDF."
        actions={
          <>
            <button type="button" className={btnGhost} onClick={() => exportToCsv("rythugaru-customers", exportColumns, reportRows)}>
              <i className="ri-file-excel-2-line"></i> CSV
            </button>
            <button type="button" className={btnPrimary} onClick={() => exportToPdf({ title: "Customer Report", subtitle: `${reportRows.length} customers`, columns: exportColumns, rows: reportRows })}>
              <i className="ri-file-pdf-2-line"></i> PDF
            </button>
          </>
        }
      />

      <StatCards
        stats={[
          { label: "Customers in view", value: summary.total, icon: "ri-user-3-line", tone: "primary" },
          { label: "Repeat customers", value: summary.repeat, icon: "ri-repeat-2-line", tone: "secondary" },
          { label: "Lifetime spend", value: formatRupees(summary.totalSpend), icon: "ri-money-rupee-circle-line", tone: "primary" },
          { label: "Avg. spend / customer", value: formatRupees(summary.avgSpend), icon: "ri-line-chart-line", tone: "accent" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name, email, phone, city…" />
          <Select label="Type" value={typeFilter} onChange={setTypeFilter} options={[{ value: "all", label: "All types" }, ...optionsFrom(VISITOR_TYPE)]} />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...optionsFrom(VISITOR_STATUS)]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{reportRows.length} customers</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Building customer report…" />
        ) : (
          <DataTable columns={columns} rows={reportRows} />
        )}
      </div>
    </div>
  );
}